import { X } from 'lucide-react';
import { usePreferencesStore } from '../../stores/preferencesStore';
import { EXAM_NOTE_OPTIONS } from '../../types';

interface SettingsModalProps {
  onClose: () => void;
}

export default function SettingsModal({ onClose }: SettingsModalProps) {
  const { preferences, updatePreferences } = usePreferencesStore();

  if (!preferences) return null;

  // Parse exam_notes CSV into a Set for easy lookup
  const activeNotes = new Set(
    (preferences.exam_notes || '')
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean)
  );

  const toggleExamNote = (key: string) => {
    const next = new Set(activeNotes);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    updatePreferences({ exam_notes: Array.from(next).join(',') });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-900 rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-surface-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Settings
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Voice Input */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Voice Input
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Mode</label>
                <div className="mt-1 flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="voiceMode"
                      checked={preferences.voice_input_mode === 'toggle'}
                      onChange={() => updatePreferences({ voice_input_mode: 'toggle' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Toggle</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="voiceMode"
                      checked={preferences.voice_input_mode === 'push_to_talk'}
                      onChange={() => updatePreferences({ voice_input_mode: 'push_to_talk' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Push-to-talk</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Transcription</label>
                <div className="mt-1 flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="transcriptionMode"
                      checked={preferences.transcription_mode === 'realtime'}
                      onChange={() => updatePreferences({ transcription_mode: 'realtime' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Real-time</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="transcriptionMode"
                      checked={preferences.transcription_mode === 'batch'}
                      onChange={() => updatePreferences({ transcription_mode: 'batch' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">After dictation</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Report Generation */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Report Generation
            </h3>
            <div className="space-y-4">
              {/* Reporting Style */}
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Reporting Style</label>
                <div className="mt-1 flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="reportingStyle"
                      checked={preferences.reporting_style === 'narrative'}
                      onChange={() => updatePreferences({ reporting_style: 'narrative' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Narrative</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="reportingStyle"
                      checked={preferences.reporting_style === 'structured'}
                      onChange={() => updatePreferences({ reporting_style: 'structured' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Structured</span>
                  </label>
                </div>
              </div>

              {/* Exam-Specific Notes */}
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Exam-Specific Notes
                </label>
                <div className="mt-2 space-y-2">
                  {EXAM_NOTE_OPTIONS.map((opt) => (
                    <label key={opt.key} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={activeNotes.has(opt.key)}
                        onChange={() => toggleExamNote(opt.key)}
                        className="mt-0.5 text-primary-600"
                      />
                      <div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {opt.label}
                        </span>
                        <p className="text-xs text-gray-500">
                          {opt.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Appearance */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Appearance
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Theme</label>
                <div className="mt-1 flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="theme"
                      checked={preferences.ui_theme === 'light'}
                      onChange={() => updatePreferences({ ui_theme: 'light' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Light</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="theme"
                      checked={preferences.ui_theme === 'dark'}
                      onChange={() => updatePreferences({ ui_theme: 'dark' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Dark</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Font Size</label>
                <select
                  value={preferences.font_size}
                  onChange={(e) => updatePreferences({ font_size: parseInt(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 bg-white dark:bg-surface-850 border border-gray-300 dark:border-surface-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="12">12px</option>
                  <option value="14">14px</option>
                  <option value="16">16px</option>
                  <option value="18">18px</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-surface-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
